/**
 * Telefonnotiz mit E-Mail-Mitteilung an den Sachbearbeiter (Mail Kanzlei
 * Polz, 30.09.2026): Payload des Dialogs „Telefonat vermerken“, Vorauswahl
 * der Empfänger und die Rückmeldung zum Versand.
 */
import type {
  CreatePhoneCallPayload,
  NotifyPayload,
  NotifyRecipient,
  NotifyResult,
} from "@/services/phoneCallsApi";
import { normalizeContactBehavior, normalizeContactBehaviorNote } from "@/utils/contactBehavior";

export interface PhoneCallForm {
  direction: "inbound" | "outbound";
  keyword: string;
  /** Datum aus dem DateInput (TT.MM.JJJJ), Uhrzeit als "HH:MM". */
  calledDate: Date | null;
  calledTime: string;
  contactName: string;
  contactPhone: string;
  /** null = nicht erfasst (Altbestand beim Bearbeiten). */
  contactReached: boolean | null;
  /** Gesprächsverhalten des Gesprächspartners (Kundenfeedback 09.10.2026). */
  contactBehavior: string[];
  contactBehaviorNote: string;
  durationMinutes: number | string | null;
  summary: string;
}

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Datum und Uhrzeit ("HH:MM") zu einem Zeitpunkt; null bei ungültiger Eingabe. */
export function combineCallDateTime(date: Date | null, time: string): Date | null {
  const match = TIME_PATTERN.exec((time ?? "").trim());
  if (!(date instanceof Date) || Number.isNaN(date.getTime()) || !match) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(match[1]), Number(match[2]));
}

/** Zeitpunkt (ISO) für die Eingabefelder in Datum und Uhrzeit aufteilen. */
export function splitCalledAt(iso?: string | null): { date: Date; time: string } {
  const parsed = iso ? new Date(iso) : new Date();
  const d = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/**
 * Anzeigename eines Beteiligten ohne angehängte E-Mail-Adresse
 * („Klinger & Kollegen <info@…>“ → „Klinger & Kollegen“).
 */
export function contactDisplayName(name: string | null | undefined): string {
  const raw = (name ?? "").trim();
  const withoutMail = raw.replace(/(\s*<[^<>@\s]+@[^<>\s]+>)+\s*$/, "").trim();
  if (withoutMail) return withoutMail;
  return (/<([^<>]+)>/.exec(raw)?.[1] ?? raw).trim();
}

const SAVED = "Telefonat gespeichert.";
const MAIL_FAILED_FALLBACK = "Die E-Mail konnte nicht versendet werden.";

function textOrNull(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return trimmed === "" ? null : trimmed;
}

/** Setzt gültige Datums- und Zeitangaben voraus (combineCallDateTime). */
export function buildPhoneCallPayload(form: PhoneCallForm): CreatePhoneCallPayload {
  const minutes = form.durationMinutes;
  const calledAt = combineCallDateTime(form.calledDate, form.calledTime) ?? new Date();
  return {
    direction: form.direction,
    keyword: textOrNull(form.keyword),
    contactName: textOrNull(form.contactName),
    contactPhone: textOrNull(form.contactPhone),
    contactReached: form.contactReached,
    contactBehavior: normalizeContactBehavior(form.contactBehavior),
    contactBehaviorNote: normalizeContactBehaviorNote(form.contactBehaviorNote),
    calledAt: calledAt.toISOString(),
    durationSeconds: minutes != null && minutes !== "" ? Math.round(Number(minutes) * 60) : null,
    summary: textOrNull(form.summary),
  };
}

/** Die Sachbearbeiter der Akte sind im Dialog vorausgewählt. */
export function preselectedRecipientIds(recipients: NotifyRecipient[] | null | undefined): number[] {
  return (recipients ?? []).filter((r) => r.isSachbearbeiter).map((r) => r.idUser);
}

/**
 * Auswahl nach einem Fallwechsel: von Hand gewählte Empfänger bleiben, soweit
 * sie im neuen Fall wählbar sind; dessen Sachbearbeiter kommen hinzu – außer
 * dem angemeldeten Nutzer selbst (selfId).
 */
export function mergeRecipientSelection(
  previous: number[],
  recipients: NotifyRecipient[],
  selfId: number | null = null,
): number[] {
  const available = new Set(recipients.map((r) => r.idUser));
  const kept = previous.filter((id) => available.has(id));
  // Sich selbst schreibt man nur bewusst an – keine automatische Vorauswahl.
  const preselected = preselectedRecipientIds(recipients).filter((id) => id !== selfId);
  return [...new Set([...kept, ...preselected])];
}

/** Auswahl im Empfängerblock: Kollegen, externe Adressen, Wiedervorlage. */
export interface NotifySelection {
  userIds: number[];
  emails: string[];
  callbackFollowup: boolean;
}

const EMAIL_PATTERN = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

/** Externe Adressen: an Komma, Semikolon und Leerzeichen trennen, klein, ohne Doppelte. */
export function normalizeExternalEmails(entries: string[]): string[] {
  const out: string[] = [];
  for (const entry of entries ?? []) {
    for (const part of String(entry).split(/[\s,;]+/)) {
      const addr = part.trim().toLowerCase();
      if (addr && !out.includes(addr)) out.push(addr);
    }
  }
  return out;
}

export function invalidExternalEmails(entries: string[]): string[] {
  return normalizeExternalEmails(entries).filter((addr) => !EMAIL_PATTERN.test(addr));
}

/**
 * Mitteilung für den Request. Ohne Empfänger: keine Mitteilung. Eine
 * Wiedervorlage hängt am Fall und an einem Kollegen – sonst entfällt sie.
 */
export function buildNotify(selection: NotifySelection, hasCase: boolean): NotifyPayload | undefined {
  const userIds = [...new Set(selection.userIds ?? [])];
  const emails = normalizeExternalEmails(selection.emails ?? []);
  if (userIds.length === 0 && emails.length === 0) return undefined;
  return { userIds, emails, callbackFollowup: hasCase && userIds.length > 0 && selection.callbackFollowup };
}

function names(users: { name: string }[]): string {
  return users.map((u) => u.name).join(", ");
}

function followupText(count: number): string {
  return count === 1 ? "1 Wiedervorlage angelegt" : `${count} Wiedervorlagen angelegt`;
}

/** Snackbar-Meldung nach dem Speichern, inklusive Versandergebnis. */
export function saveResultMessage(
  notification: NotifyResult | null | undefined,
): { message: string; type: "success" | "warning" } {
  if (!notification) return { message: SAVED, type: "success" };
  const result = notifyResultMessage(notification);
  return { message: `${SAVED} ${result.message}`, type: result.type };
}

/** Snackbar-Meldung zum Versandergebnis einer Mitteilung. */
export function notifyResultMessage(notification: NotifyResult): { message: string; type: "success" | "warning" } {
  const parts: string[] = [];
  if (notification.sent.length > 0) {
    let sent = `Telefonnotiz an ${names(notification.sent)} gesendet`;
    if (notification.followupsCreated > 0) sent += `, ${followupText(notification.followupsCreated)}`;
    parts.push(`${sent}.`);
  }
  if (notification.failed.length > 0) {
    const failed = notification.failed
      .map((f) => `${f.name}: ${(f.reason || "").trim() || MAIL_FAILED_FALLBACK}`)
      .join("; ");
    parts.push(`Nicht zugestellt an ${failed}`);
  }
  return { message: parts.join(" "), type: notification.failed.length > 0 ? "warning" : "success" };
}
